/**
 * Responsive Layout Utilities
 * 
 * Utilities for handling responsive design patterns across the application.
 * Provides hooks and helpers for breakpoint-based logic.
 */

import { useTheme, useMediaQuery, Theme } from '@mui/material';
import { tokens } from '../theme/tokens';

/**
 * Breakpoint type definition
 */
export type Breakpoint = 'mobile' | 'tablet' | 'desktop';

/**
 * Hook to get the current breakpoint
 * 
 * @returns Current breakpoint ('mobile' | 'tablet' | 'desktop')
 * 
 * @example
 * const breakpoint = useBreakpoint();
 * if (breakpoint === 'mobile') {
 *   // Render mobile layout
 * }
 */
export const useBreakpoint = (): Breakpoint => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // < 768px
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg')); // 768px - 1024px

  if (isMobile) return 'mobile';
  if (isTablet) return 'tablet';
  return 'desktop';
};

/**
 * Hook to check if viewport is mobile
 * 
 * @returns true if viewport width < 768px
 */
export const useIsMobile = (): boolean => {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down('md'));
};

/**
 * Hook to check if viewport is tablet
 * 
 * @returns true if viewport width is between 768px and 1024px
 */
export const useIsTablet = (): boolean => {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.between('md', 'lg'));
};

/**
 * Hook to check if viewport is desktop
 * 
 * @returns true if viewport width >= 1024px
 */
export const useIsDesktop = (): boolean => {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.up('lg'));
};

/**
 * Hook to check if viewport is mobile or tablet
 * 
 * @returns true if viewport width < 1024px
 */
export const useIsMobileOrTablet = (): boolean => {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down('lg'));
};

/**
 * Get responsive value based on current breakpoint
 * 
 * @param mobile - Value for mobile breakpoint
 * @param tablet - Value for tablet breakpoint
 * @param desktop - Value for desktop breakpoint
 * @returns Value corresponding to current breakpoint
 * 
 * @example
 * const columns = useResponsiveValue(1, 2, 3);
 * // Returns 1 on mobile, 2 on tablet, 3 on desktop
 */
export const useResponsiveValue = <T,>(
  mobile: T,
  tablet: T,
  desktop: T
): T => {
  const breakpoint = useBreakpoint();
  
  switch (breakpoint) {
    case 'mobile':
      return mobile;
    case 'tablet':
      return tablet;
    case 'desktop':
      return desktop;
  }
};

/**
 * Get responsive spacing value
 * 
 * @param mobile - Spacing for mobile (in spacing units)
 * @param tablet - Spacing for tablet (in spacing units)
 * @param desktop - Spacing for desktop (in spacing units)
 * @returns Spacing value in pixels
 * 
 * @example
 * const padding = getResponsiveSpacing(2, 3, 4);
 * // Returns 8px on mobile, 12px on tablet, 16px on desktop
 */
export const getResponsiveSpacing = (
  mobile: number,
  tablet: number,
  desktop: number,
  breakpoint: Breakpoint
): number => {
  const spacingMap = {
    mobile,
    tablet,
    desktop,
  };
  
  return spacingMap[breakpoint] * tokens.spacing.unit;
};

/**
 * Responsive grid columns helper
 * 
 * @param breakpoint - Current breakpoint
 * @returns Number of columns for the breakpoint
 */
export const getResponsiveColumns = (breakpoint: Breakpoint): number => {
  const columnMap = {
    mobile: 1,
    tablet: 2,
    desktop: 3,
  };
  
  return columnMap[breakpoint];
};

/**
 * Check if touch device
 * 
 * @returns true if device supports touch
 */
export const isTouchDevice = (): boolean => {
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    // @ts-ignore - msMaxTouchPoints is IE specific
    navigator.msMaxTouchPoints > 0
  );
};

/**
 * Get minimum touch target size (44x44px for mobile)
 * 
 * @param breakpoint - Current breakpoint
 * @returns Minimum size in pixels
 */
export const getMinTouchTargetSize = (breakpoint: Breakpoint): number => {
  return breakpoint === 'mobile' ? 44 : 36;
};

/**
 * Responsive container max-width helper
 * 
 * @param breakpoint - Current breakpoint
 * @returns Max width in pixels
 */
export const getContainerMaxWidth = (breakpoint: Breakpoint): number => {
  const widthMap = {
    mobile: 768,
    tablet: 1024,
    desktop: 1440,
  };
  
  return widthMap[breakpoint];
};

/**
 * Media query strings for use in styled components
 */
export const mediaQueries = {
  mobile: `@media (max-width: ${tokens.breakpoints.mobile - 1}px)`,
  tablet: `@media (min-width: ${tokens.breakpoints.mobile}px) and (max-width: ${tokens.breakpoints.tablet - 1}px)`,
  desktop: `@media (min-width: ${tokens.breakpoints.desktop}px)`,
  mobileAndTablet: `@media (max-width: ${tokens.breakpoints.desktop - 1}px)`,
  tabletAndDesktop: `@media (min-width: ${tokens.breakpoints.mobile}px)`,
};

/**
 * Responsive font size helper
 * 
 * @param breakpoint - Current breakpoint
 * @param baseSize - Base font size in rem
 * @returns Adjusted font size in rem
 */
export const getResponsiveFontSize = (
  breakpoint: Breakpoint,
  baseSize: number
): number => {
  const scaleMap = {
    mobile: 0.875, // 87.5% of base
    tablet: 0.9375, // 93.75% of base
    desktop: 1, // 100% of base
  };
  
  return baseSize * scaleMap[breakpoint];
};
